import type { SeopMonth, SeopRestoreSkipped, SeopScenario, SeopScenarioRow, SeopSegmentTotal } from '@/types/seop';
import { seopNomeDoMes, seopNum } from '@/utils/seopFormat';

/**
 * Busca, filtro e ordenação da tabela "Cenário do período".
 *
 * Fora do componente porque é regra, não tela — e porque é aqui que mora a única coisa
 * delicada: a ordem PADRÃO é a do motor (`order` do produto), e é ela que a pessoa
 * reconhece. Por isso `ordenarPor: null` não ordena nada; só a escolha explícita
 * reordena, e nunca no lugar (o array do cálculo continua intacto).
 */

/** As colunas que aceitam ordenação. Texto e número são comparados de formas diferentes. */
export type ChaveDeOrdenacao =
  | 'code'
  | 'description'
  | 'segment'
  | 'net_qty'
  | 'unit_value'
  | 'cpv_unit_value'
  | 'margin_percent'
  | 'unit_margin_value'
  | 'final_value'
  | 'contribution_value'
  | 'difference';

const TEXTO: ChaveDeOrdenacao[] = ['code', 'description', 'segment'];

export interface FiltroDoCenario {
  busca: string;
  /** Linha de produção; `''` = todas. */
  linha: string;
  ordenarPor: ChaveDeOrdenacao | null;
  direcao: 'asc' | 'desc';
}

export const FILTRO_INICIAL: FiltroDoCenario = {
  busca: '',
  linha: '',
  ordenarPor: null,
  direcao: 'desc',
};

/** Sem acento e sem caixa — quem apresenta digita "fisiologico", não "FISIOLÓGICO". */
function normalizar(texto: string): string {
  return (texto ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}

function casaBusca(linha: SeopScenarioRow, termo: string): boolean {
  const alvo = normalizar(`${linha.code} ${linha.description} ${linha.segment}`);
  return normalizar(termo)
    .split(/\s+/)
    .every((parte) => alvo.includes(parte));
}

function comparar(a: SeopScenarioRow, b: SeopScenarioRow, chave: ChaveDeOrdenacao): number {
  if (TEXTO.includes(chave)) {
    return String(a[chave] ?? '').localeCompare(String(b[chave] ?? ''), 'pt-BR');
  }
  return seopNum(a[chave] as never) - seopNum(b[chave] as never);
}

/** Linha sem número na coluna escolhida (o wire manda `NaN` como string). */
function semNumero(linha: SeopScenarioRow, chave: ChaveDeOrdenacao): boolean {
  return !TEXTO.includes(chave) && Number.isNaN(seopNum(linha[chave] as never));
}

/** As linhas que a tabela mostra: filtradas pela busca e pela linha, depois ordenadas. */
export function linhasVisiveis(
  linhas: SeopScenarioRow[],
  filtro: FiltroDoCenario,
): SeopScenarioRow[] {
  const termo = filtro.busca.trim();
  const visiveis = linhas.filter(
    (l) => (!filtro.linha || l.segment === filtro.linha) && (!termo || casaBusca(l, termo)),
  );
  if (!filtro.ordenarPor) return visiveis;
  const chave = filtro.ordenarPor;
  const sinal = filtro.direcao === 'asc' ? 1 : -1;
  return [...visiveis].sort((a, b) => {
    // Linha sem número vai para o FIM nas duas direções: ela não diz nada sobre o
    // ranking, e liderar a lista crescente seria pior do que não ordenar.
    const [va, vb] = [semNumero(a, chave), semNumero(b, chave)];
    if (va || vb) return va && vb ? 0 : va ? 1 : -1;
    return sinal * comparar(a, b, chave);
  });
}

/** As linhas de produção presentes no cálculo, para o seletor — vêm do dado, não fixas. */
export function linhasDeProducao(linhas: SeopScenarioRow[]): string[] {
  return [...new Set(linhas.map((l) => l.segment).filter(Boolean))].sort();
}

/** O clique no cabeçalho: mesma coluna inverte a direção, coluna nova começa decrescente. */
export function alternarOrdenacao(filtro: FiltroDoCenario, chave: ChaveDeOrdenacao): FiltroDoCenario {
  if (filtro.ordenarPor !== chave) return { ...filtro, ordenarPor: chave, direcao: 'desc' };
  return { ...filtro, direcao: filtro.direcao === 'desc' ? 'asc' : 'desc' };
}

// --------------------------------------------------------------- recorte do filtro

/**
 * O nome do recorte em tela (`SPGV`, `busca "soro"`, `SPGV · busca "soro"`). Vazio quando
 * não há busca nem linha escolhida — ordenar muda a ordem, não recorta nada.
 */
export function rotuloDoRecorte(filtro: FiltroDoCenario): string {
  const termo = filtro.busca.trim();
  return [filtro.linha, termo ? `busca "${termo}"` : ''].filter(Boolean).join(' · ');
}

const finito = (v: number): number => (Number.isFinite(v) ? v : 0);

/**
 * A soma de um conjunto de linhas, com a MESMA regra do motor (`scenario_overall_total`):
 * margem = Σ margem ÷ Σ receita — ponderada pela receita, nunca a média dos percentuais.
 *
 * Existe para os cartões e o "Total exibido" acompanharem o filtro (pedido do Herrison,
 * out/26): o motor só manda o total do período e o de cada linha de produção, e uma busca
 * recorta qualquer subconjunto. Sem filtro, o resultado é igual ao `economics.total`.
 */
export function totalDasLinhas(linhas: SeopScenarioRow[], segment = 'TOTAL'): SeopSegmentTotal {
  const soma = (campo: 'gross_qty' | 'net_qty' | 'final_value' | 'cpv_value' | 'contribution_value'): number =>
    linhas.reduce((s, l) => s + finito(seopNum(l[campo])), 0);
  const receita = soma('final_value');
  const margem = soma('contribution_value');
  return {
    segment,
    gross_qty: soma('gross_qty'),
    net_qty: soma('net_qty'),
    final_value: receita,
    cpv_value: soma('cpv_value'),
    contribution_value: margem,
    margin_rate: receita ? margem / receita : 0,
  };
}

// --------------------------------------------------------------- cenários salvos

/** Prefixo que o backend põe no snapshot criado antes de restaurar (`BACKUP_PREFIX`). */
export const PREFIXO_BACKUP = 'Backup automático';

export const ehBackup = (s: SeopScenario): boolean => s.name.startsWith(PREFIXO_BACKUP);

/** Separa o que a pessoa salvou dos backups automáticos; a ordem (mais novo primeiro) é a do backend. */
export function separarBackups(scenarios: SeopScenario[]): { salvos: SeopScenario[]; backups: SeopScenario[] } {
  return { salvos: scenarios.filter((s) => !ehBackup(s)), backups: scenarios.filter(ehBackup) };
}

/** O cenário mais recente que alguém salvou — backup automático não conta. */
export function ultimoSalvo(scenarios: SeopScenario[]): SeopScenario | null {
  return scenarios.find((s) => !ehBackup(s)) ?? null;
}

/** `out/26` no lugar do índice cru: "Mês 1" não diz a ninguém de que mês é o cenário. */
export function rotuloDoMes(months: SeopMonth[], index: number): string {
  const mes = months.find((m) => m.index === index);
  return mes ? seopNomeDoMes(mes) : `M${index + 1}`;
}

/**
 * O mês em que o cenário foi salvo. Vem pronto do backend (`month.name`), que segue o mês
 * pelo id mesmo depois de uma correção de data; a posição (`month_index`) só serve de
 * recurso para resposta de backend antigo.
 */
export function mesDoCenario(s: SeopScenario, months: SeopMonth[]): string {
  return s.month?.name || rotuloDoMes(months, s.month_index);
}

/** O mês do cenário saiu do plano: não há onde abri-lo nem aplicá-lo até o mês ser corrigido. */
export const foraDoPlano = (s: SeopScenario): boolean => s.month !== undefined && s.month.id === null;

/** Próximo nome da série (`Cenário Outubro V2` → `Cenário Outubro V3`); vazio se não há série. */
export function sugerirNome(scenarios: SeopScenario[]): string {
  const serie = ultimoSalvo(scenarios)?.name.match(/^(.*\S)\s*V(\d+)$/i);
  return serie ? `${serie[1]} V${Number(serie[2]) + 1}` : '';
}

// --------------------------------------------------------------- restauração

/** O nome do snapshot que o desfazer cria; começa com o prefixo para cair no grupo dos backups. */
export const nomeDoBackupDeDesfazer = (restaurado: string): string =>
  `${PREFIXO_BACKUP} — antes de desfazer "${restaurado}"`;

function enumerar(itens: string[], limite = 5): string {
  const resto = itens.length - limite;
  return itens.slice(0, limite).join(', ') + (resto > 0 ? ` e mais ${resto}` : '');
}

const RESSALVAS: { campo: keyof SeopRestoreSkipped; texto: string }[] = [
  { campo: 'products_missing', texto: 'Produtos do cenário que não existem mais no cadastro (ficaram de fora)' },
  { campo: 'resources_missing', texto: 'Máquinas do cenário que não existem mais (ficaram de fora)' },
  { campo: 'products_kept', texto: 'Produtos criados depois do cenário (ficaram como estão)' },
  { campo: 'resources_kept', texto: 'Máquinas criadas depois do cenário (ficaram como estão)' },
];

/**
 * O que a restauração não trouxe, ou deixou como estava, em frases para a tela. Vazio quando
 * o plano de hoje tem exatamente os produtos e as máquinas do cenário — aí o mês ficou igual
 * à foto e não há ressalva a fazer.
 */
export function ressalvasDaRestauracao(skipped?: SeopRestoreSkipped): string[] {
  if (!skipped) return [];
  return RESSALVAS.filter((r) => (skipped[r.campo] ?? []).length > 0).map(
    (r) => `${r.texto}: ${enumerar(skipped[r.campo])}.`,
  );
}
